import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { UploadCenterProvider } from '@/contexts/UploadCenterContext';
import { CartProvider } from '@/contexts/CartContext';
import { LanguageProvider, LanguageRemount, LanguageAccountSync, tr } from '@/contexts/LanguageContext';
import { FeatureFlagsProvider, useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { PwaInstallProvider } from '@/pwa/PwaInstallContext';
import { InstallBanner, InstallGuideDialog } from '@/pwa/InstallPrompt';
import { Toaster } from '@/components/ui/sonner';
import { DropLayer } from '@/components/Drops/DropLayer';
import { UploadCenterTopBar, UploadCenterBanner } from '@/components/Upload/UploadCenterBanner';

// Pages
const Landing = lazy(() => import('@/pages/Landing'));
const Auth = lazy(() => import('@/pages/Auth'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const ConsentSign = lazy(() => import('@/pages/ConsentSign'));
const ConsentRevoke = lazy(() => import('@/pages/ConsentRevoke'));
const ConsentBarber = lazy(() => import('@/pages/ConsentBarber'));
const Feed = lazy(() => import('@/pages/Feed'));
const Reels = lazy(() => import('@/pages/Reels'));
const Rankings = lazy(() => import('@/pages/Rankings'));
const Profile = lazy(() => import('@/pages/Profile'));
// AdminDashboard fue fusionado con AdminProfileView (visible en /profile cuando role=admin).
// /admin redirige automáticamente a /profile vía AdminRedirect.
const DiscoverBarbers = lazy(() => import('@/pages/DiscoverBarbers'));
const BarberProfile = lazy(() => import('@/pages/BarberProfile'));
const BookingFlow = lazy(() => import('@/pages/BookingFlow'));
const BookingEntry = lazy(() => import('@/pages/BookingEntry'));
const ActivateAccount = lazy(() => import('@/pages/ActivateAccount'));
const AccessLink = lazy(() => import('@/pages/AccessLink'));
const CreatePassword = lazy(() => import('@/pages/CreatePassword'));
import { PasswordRequiredDialog } from '@/components/Client/PasswordRequiredDialog';
import { StepUpDialog } from '@/components/Security/StepUpDialog';
import { AnalyticsConsentBanner } from '@/components/Legal/AnalyticsConsentBanner';
import { ConsentPrompt } from '@/components/Consent/ConsentPrompt';
const MyBookings = lazy(() => import('@/pages/MyBookings'));
const GuestBooking = lazy(() => import('@/pages/GuestBooking'));
const PayQr = lazy(() => import('@/pages/PayQr'));
const PrizeWallet = lazy(() => import('@/pages/PrizeWallet'));
const SettingsBookings = lazy(() => import('@/pages/SettingsBookings'));
const SettingsBookingsServices = lazy(() => import('@/pages/SettingsBookingsServices'));
const SettingsBookingsSchedule = lazy(() => import('@/pages/SettingsBookingsSchedule'));
const SettingsBookingsOnline = lazy(() => import('@/pages/SettingsBookingsOnline'));
const SettingsBookingsRules = lazy(() => import('@/pages/SettingsBookingsRules'));
const SettingsBookingsAgenda = lazy(() => import('@/pages/SettingsBookingsAgenda'));
const SettingsBookingsClients = lazy(() => import('@/pages/SettingsBookingsClients'));
const SettingsBookingsPayments = lazy(() => import('@/pages/SettingsBookingsPayments'));
const SettingsBookingsAddress = lazy(() => import('@/pages/SettingsBookingsAddress'));
const SettingsBarbershop = lazy(() => import('@/pages/SettingsBarbershop'));
const ShopDashboard = lazy(() => import('@/pages/ShopDashboard'));
const ShopPayments = lazy(() => import('@/pages/ShopPayments'));
const ShopProfile = lazy(() => import('@/pages/ShopProfile'));
const ShopInviteBarbers = lazy(() => import('@/pages/ShopInviteBarbers'));
const ShopTemplates = lazy(() => import('@/pages/ShopTemplates'));
const ShopAgenda = lazy(() => import('@/pages/ShopAgenda'));
const ShopHours = lazy(() => import('@/pages/ShopHours'));
const WorkRequests = lazy(() => import('@/pages/WorkRequests'));
const PostDetail = lazy(() => import('@/pages/PostDetail'));
const Tournaments = lazy(() => import('@/pages/Tournaments'));
const VerifyPrize = lazy(() => import('@/pages/VerifyPrize'));
const MyMoney = lazy(() => import('@/pages/WalletFinance'));
const Stats = lazy(() => import('@/pages/Stats'));
const StatsExpenses = lazy(() => import('@/pages/StatsExpenses'));
const TournamentDetail = lazy(() => import('@/pages/TournamentDetail'));
const BrandDashboard = lazy(() => import('@/pages/BrandDashboard'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const NotificationSettings = lazy(() => import('@/pages/NotificationSettings'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const Connections = lazy(() => import('@/pages/Connections'));
const ProfessionalPanel = lazy(() => import('@/pages/ProfessionalPanel'));
const Settings = lazy(() => import('@/pages/Settings'));
const SettingsAccount = lazy(() => import('@/pages/SettingsAccount'));
const SettingsProfile = lazy(() => import('@/pages/SettingsProfile'));
const EmbeddedStripeCheckout = lazy(() => import('@/pages/EmbeddedStripeCheckout'));
const FinanzasLineage = lazy(() => import('@/pages/admin/FinanzasLineage'));
const SettingsPrivacy = lazy(() => import('@/pages/SettingsPrivacy'));
const SettingsSecurity = lazy(() => import('@/pages/SettingsSecurity'));
const Directory = lazy(() => import('@/pages/Directory'));
const SettingsPayment = lazy(() => import('@/pages/SettingsPayment'));
const SettingsVerification = lazy(() => import('@/pages/SettingsVerification'));
const SellerVerification = lazy(() => import('@/pages/SellerVerification'));
const SellerProducts = lazy(() => import('@/pages/SellerProducts'));
const SellerCourses = lazy(() => import('@/pages/SellerCourses'));
const CoursePlayer = lazy(() => import('@/pages/CoursePlayer'));
const SettingsLanguage = lazy(() => import('@/pages/SettingsLanguage'));
const SettingsAppearance = lazy(() => import('@/pages/SettingsAppearance'));
const SettingsPlaceholder = lazy(() => import('@/pages/SettingsPlaceholder'));
const Search = lazy(() => import('@/pages/Search'));
import NotFound from '@/pages/NotFound';
import ShopRedirect from '@/components/ShopRedirect';
const MembershipSettings = lazy(() => import('@/pages/MembershipSettings'));
const ConnectClaude = lazy(() => import('@/pages/ConnectClaude'));
const CardPricingSettings = lazy(() => import('@/pages/CardPricingSettings'));
const CardPricingSign = lazy(() => import('@/pages/CardPricingSign'));
const OAuthAuthorize = lazy(() => import('@/pages/OAuthAuthorize'));
const Cart = lazy(() => import('@/pages/Cart'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const ClientDashboard = lazy(() => import('@/pages/ClientDashboard'));
const LikedHaircuts = lazy(() => import('@/pages/LikedHaircuts'));
const GiftHistory = lazy(() => import('@/pages/GiftHistory'));
const TokenWallet = lazy(() => import('@/pages/TokenWallet'));
const Leagues = lazy(() => import('@/pages/Leagues'));
const LeagueLadder = lazy(() => import('@/pages/LeagueLadder'));
const ChampionsWall = lazy(() => import('@/pages/ChampionsWall'));
const CreditsStore = lazy(() => import('@/pages/CreditsStore'));
const ChallengeDetail = lazy(() => import('@/pages/ChallengeDetail'));
const SupportRanking = lazy(() => import('@/pages/SupportRanking'));
const TopEarners = lazy(() => import('@/pages/TopEarners'));
const ModelApplication = lazy(() => import('@/pages/ModelApplication'));
const MyApplications = lazy(() => import('@/pages/MyApplications'));
const Support = lazy(() => import('@/pages/Support'));
const JudgeTraining = lazy(() => import('@/pages/JudgeTraining'));
const AdminTournamentControl = lazy(() => import('@/pages/AdminTournamentControl'));
const BracketLive = lazy(() => import('@/pages/BracketLive'));
const TournamentOps = lazy(() => import('@/pages/TournamentOps'));
const KoJudgePanel = lazy(() => import('@/pages/KoJudgePanel'));
const GroupJudgePanel = lazy(() => import('@/pages/GroupJudgePanel'));
const JudgeOnboarding = lazy(() => import('@/pages/JudgeOnboarding'));
const JudgeHome = lazy(() => import('@/pages/JudgeHome'));
const JudgeMyReputation = lazy(() => import('@/pages/JudgeMyReputation'));
const JudgeReputation = lazy(() => import('@/pages/JudgeReputation'));
const JudgeHistoryDetail = lazy(() => import('@/pages/JudgeHistoryDetail'));
const EvidenceUpload = lazy(() => import('@/pages/EvidenceUpload'));
const EvidenceReview = lazy(() => import('@/pages/EvidenceReview'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));
const AdminWithdrawals = lazy(() => import('@/pages/AdminWithdrawals'));
const AdminPostsModeration = lazy(() => import('@/pages/AdminPostsModeration'));
const AdminStorage = lazy(() => import('@/pages/AdminStorage'));
const LivePoc = lazy(() => import('@/pages/LivePoc'));
const AdminLiveRubric = lazy(() => import('@/pages/AdminLiveRubric'));
const LivePhotos = lazy(() => import('@/pages/LivePhotos'));
const LiveJudgePhotos = lazy(() => import('@/pages/LiveJudgePhotos'));
const LiveResults = lazy(() => import('@/pages/LiveResults'));
const LiveBarberSession = lazy(() => import('@/pages/LiveBarberSession'));
const LiveJudgeSession = lazy(() => import('@/pages/LiveJudgeSession'));
const LiveMonitor = lazy(() => import('@/pages/LiveMonitor'));
const LiveWizard = lazy(() => import('@/pages/LiveWizard'));
const LiveSandbox = lazy(() => import('@/pages/LiveSandbox'));
const MessagesInbox = lazy(() => import('@/pages/Messages/Inbox'));
const ChatThread = lazy(() => import('@/pages/Messages/ChatThread'));
import { AdminSettingsRedirect, AdminContentRedirect } from '@/components/Admin/AdminRedirect';
import { GlobalCreateButton } from '@/components/Create/GlobalCreateButton';
import { AgendaFab } from '@/components/Agenda/AgendaFab';
const BarbersNearby = lazy(() => import('@/pages/BarbersNearby'));
import { FeedbackButton } from '@/components/Feedback/FeedbackButton';
import { ViewAsBanner } from '@/components/Layout/ViewAsBanner';
import { DemoImpersonationBanner } from '@/components/Layout/DemoImpersonationBanner';
import { AppErrorBoundary } from '@/components/Layout/AppErrorBoundary';
import { installGlobalErrorCapture } from '@/lib/clientErrors';

installGlobalErrorCapture();
import { LegalAcceptanceGate } from '@/components/Legal/LegalAcceptanceGate';
const LegalDoc = lazy(() => import('@/pages/LegalDoc'));
const LegalContact = lazy(() => import('@/pages/LegalContact'));
const ConfirmEmailChange = lazy(() => import('@/pages/ConfirmEmailChange'));
const CourseDetail = lazy(() => import('@/pages/CourseDetail'));
const ShopCampaigns = lazy(() => import('@/pages/ShopCampaigns'));
const ShopTournamentRequest = lazy(() => import('@/pages/ShopTournamentRequest'));
const GiftCardPage = lazy(() => import('@/pages/GiftCardPage'));
const ReviewLink = lazy(() => import('@/pages/ReviewLink'));

// Carga por ruta (rendimiento C): cada pantalla se descarga al abrirla.
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background" data-testid="page-loader">
    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{tr("Cargando...")}</p>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/auth" />;
};

// Interruptor maestro apagado ⇒ la ruta no existe (404), igual que la API.
const FeatureRoute = ({ flag, children }) => {
  const { flags, loaded } = useFeatureFlags();
  if (!loaded) return null;
  return flags[flag] ? children : <NotFound />;
};

function AppContent() {
  return (
    <>
      <UploadCenterTopBar />
      <UploadCenterBanner />
      <ViewAsBanner />
      <DemoImpersonationBanner />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/checkout/embedded-form" element={<ProtectedRoute><EmbeddedStripeCheckout /></ProtectedRoute>} />
        <Route path="/admin/finanzas" element={<ProtectedRoute><FinanzasLineage /></ProtectedRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/confirmar-email" element={<ConfirmEmailChange />} />
        <Route path="/legal/terminos" element={<LegalDoc kind="terms" />} />
        <Route path="/legal/privacidad" element={<LegalDoc kind="privacy" />} />
        <Route path="/legal/contacto" element={<LegalContact />} />
        <Route path="/consent/revoke" element={<ConsentRevoke />} />
        <Route path="/consent/barbero" element={<ConsentBarber />} />
        <Route path="/consent/:token" element={<ConsentSign />} />
        <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
        <Route path="/reels" element={<ProtectedRoute><Reels /></ProtectedRoute>} />
        {/* Legacy redirect — old Ziyup route now points to the unified feed */}
        <Route path="/lineage-feed" element={<Navigate to="/feed" replace />} />
        {/* L6 · Bloque 2 (jun-2026) — el sistema legacy de batallas/retos 1v1
            fue retirado. Redirects para no dejar enlaces muertos en
            notificaciones/mensajes viejos. */}
        <Route path="/1vs1" element={<Navigate to="/leagues" replace />} />
        <Route path="/1vs1/*" element={<Navigate to="/leagues" replace />} />
        <Route path="/battles" element={<Navigate to="/leagues" replace />} />
        <Route path="/challenges" element={<Navigate to="/leagues" replace />} />
        <Route path="/battle/:battleId" element={<Navigate to="/leagues" replace />} />
        <Route path="/live-battles" element={<Navigate to="/leagues" replace />} />
        <Route path="/live-battle/:battleId" element={<Navigate to="/leagues" replace />} />
        <Route path="/tournaments" element={<ProtectedRoute><Tournaments /></ProtectedRoute>} />
        <Route path="/tournament/:tournamentId" element={<ProtectedRoute><TournamentDetail /></ProtectedRoute>} />
        <Route path="/verificar-premio/:folio" element={<VerifyPrize />} />
        {/* B2 — cobro presencial por QR: página PÚBLICA (el cliente no necesita cuenta) */}
        <Route path="/pagar/:token" element={<PayQr />} />
        {/* A1 — billetera de Tournament Tokens (canje bloqueado por revisión legal) */}
        <Route path="/wallet/premios" element={<ProtectedRoute><PrizeWallet /></ProtectedRoute>} />
        <Route path="/money" element={<ProtectedRoute><MyMoney /></ProtectedRoute>} />
        <Route path="/estadisticas" element={<ProtectedRoute><Stats /></ProtectedRoute>} />
        <Route path="/estadisticas/gastos" element={<ProtectedRoute><StatsExpenses /></ProtectedRoute>} />
        <Route path="/professional/finanzas" element={<ProtectedRoute><MyMoney /></ProtectedRoute>} />
        <Route path="/brand" element={<ProtectedRoute><BrandDashboard /></ProtectedRoute>} />
        <Route path="/rankings" element={<ProtectedRoute><Rankings /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/barber/:barberId" element={<ProtectedRoute><BarberProfile /></ProtectedRoute>} />
        <Route path="/barber/:barberId/reservar" element={<BookingFlow />} />
        {/* Bloque 3 — link corto y QR de reservas (público): barbero ⇒ reserva directa · barbería ⇒ elegir barbero */}
        <Route path="/r/:barberId" element={<BookingEntry />} />
        <Route path="/activar-cuenta" element={<ActivateAccount />} />
        <Route path="/acceso" element={<AccessLink />} />
        <Route path="/crear-contrasena" element={<ProtectedRoute><CreatePassword /></ProtectedRoute>} />
        <Route path="/cita/:token" element={<GuestBooking />} />
        <Route path="/reservas" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
        <Route path="/post/:postId" element={<ProtectedRoute><PostDetail /></ProtectedRoute>} />
        {/* Legacy alias — old links may use /posts/:id */}
        <Route path="/posts/:postId" element={<ProtectedRoute><PostDetail /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminContentRedirect><AdminPanel /></AdminContentRedirect></ProtectedRoute>} />
        <Route path="/admin/withdrawals" element={<ProtectedRoute><AdminWithdrawals /></ProtectedRoute>} />
        <Route path="/admin/moderation" element={<ProtectedRoute><AdminPostsModeration /></ProtectedRoute>} />
        <Route path="/admin/storage" element={<ProtectedRoute><AdminStorage /></ProtectedRoute>} />
        <Route path="/admin/live-poc" element={<ProtectedRoute><LivePoc /></ProtectedRoute>} />
        <Route path="/admin/live-rubric" element={<ProtectedRoute><AdminLiveRubric /></ProtectedRoute>} />
        <Route path="/live/:tournamentId/photos" element={<ProtectedRoute><LivePhotos /></ProtectedRoute>} />
        <Route path="/live/:tournamentId/judge-photos" element={<ProtectedRoute><LiveJudgePhotos /></ProtectedRoute>} />
        <Route path="/live/:tournamentId/results" element={<ProtectedRoute><LiveResults /></ProtectedRoute>} />
        <Route path="/live/:tournamentId/session" element={<ProtectedRoute><LiveBarberSession /></ProtectedRoute>} />
        <Route path="/live/:tournamentId/judge-live" element={<ProtectedRoute><LiveJudgeSession /></ProtectedRoute>} />
        <Route path="/admin/live-monitor/:tournamentId" element={<ProtectedRoute><LiveMonitor /></ProtectedRoute>} />
        <Route path="/admin/live-wizard" element={<ProtectedRoute><LiveWizard /></ProtectedRoute>} />
        <Route path="/admin/live-sandbox" element={<ProtectedRoute><LiveSandbox /></ProtectedRoute>} />
        <Route path="/admin/live-sandbox/:tournamentId" element={<ProtectedRoute><LiveSandbox /></ProtectedRoute>} />
        <Route path="/admin/settings/global" element={<ProtectedRoute><AdminSettingsRedirect /></ProtectedRoute>} />
        <Route path="/admin/settings/personal" element={<Navigate to="/settings/account" replace />} />
        <Route path="/discover" element={<ProtectedRoute><DiscoverBarbers /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesInbox /></ProtectedRoute>} />
        <Route path="/messages/:conversationId" element={<ProtectedRoute><ChatThread /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        <Route path="/notifications/settings" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
        <Route path="/course/:courseId" element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
        <Route path="/product/:productId" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
        <Route path="/professional" element={<ProtectedRoute><ProfessionalPanel /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/settings/citas" element={<ProtectedRoute><SettingsBookings /></ProtectedRoute>} />
        <Route path="/settings/citas/servicios" element={<ProtectedRoute><SettingsBookingsServices /></ProtectedRoute>} />
        <Route path="/settings/citas/horarios" element={<ProtectedRoute><SettingsBookingsSchedule /></ProtectedRoute>} />
        <Route path="/settings/citas/reservas" element={<ProtectedRoute><SettingsBookingsOnline /></ProtectedRoute>} />
        <Route path="/settings/citas/reglas" element={<ProtectedRoute><SettingsBookingsRules /></ProtectedRoute>} />
        <Route path="/settings/citas/agenda" element={<ProtectedRoute><SettingsBookingsAgenda /></ProtectedRoute>} />
        <Route path="/settings/citas/clientes" element={<ProtectedRoute><SettingsBookingsClients /></ProtectedRoute>} />
        <Route path="/settings/citas/pagos" element={<ProtectedRoute><SettingsBookingsPayments /></ProtectedRoute>} />
        <Route path="/settings/citas/direccion" element={<ProtectedRoute><SettingsBookingsAddress /></ProtectedRoute>} />
        <Route path="/settings/barberia" element={<ProtectedRoute><SettingsBarbershop /></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><ShopDashboard /></ProtectedRoute>} />
        <Route path="/shop/pagos" element={<ProtectedRoute><ShopPayments /></ProtectedRoute>} />
        <Route path="/shop/agenda" element={<ProtectedRoute><ShopAgenda /></ProtectedRoute>} />
        <Route path="/shop/horario" element={<ProtectedRoute><ShopHours /></ProtectedRoute>} />
        <Route path="/shop/invitar" element={<ProtectedRoute><FeatureRoute flag="work_requests"><ShopInviteBarbers /></FeatureRoute></ProtectedRoute>} />
        <Route path="/shop/plantillas" element={<ProtectedRoute><FeatureRoute flag="work_requests"><ShopTemplates /></FeatureRoute></ProtectedRoute>} />
        <Route path="/shop/campanas" element={<ProtectedRoute><ShopCampaigns /></ProtectedRoute>} />
        <Route path="/mis-campanas" element={<ProtectedRoute><ShopCampaigns /></ProtectedRoute>} />
        <Route path="/shop/solicitar-torneo" element={<ProtectedRoute><ShopTournamentRequest /></ProtectedRoute>} />
        <Route path="/regalo/tarjeta" element={<ProtectedRoute><GiftCardPage /></ProtectedRoute>} />
        <Route path="/resena/:token" element={<ReviewLink />} />
        <Route path="/solicitudes" element={<ProtectedRoute><FeatureRoute flag="work_requests"><WorkRequests /></FeatureRoute></ProtectedRoute>} />
        <Route path="/shop/:username" element={<ShopProfile />} />
        <Route path="/settings/account" element={<ProtectedRoute><SettingsAccount /></ProtectedRoute>} />
        <Route path="/settings/profile" element={<ProtectedRoute><SettingsProfile /></ProtectedRoute>} />
        <Route path="/settings/privacy" element={<ProtectedRoute><SettingsPrivacy /></ProtectedRoute>} />
        <Route path="/settings/security" element={<ProtectedRoute><SettingsSecurity /></ProtectedRoute>} />
        <Route path="/directorio" element={<ProtectedRoute><Directory /></ProtectedRoute>} />
        <Route path="/shop/clientes" element={<ProtectedRoute><Directory shop /></ProtectedRoute>} />
        <Route path="/settings/payment" element={<ProtectedRoute><SettingsPayment /></ProtectedRoute>} />
        <Route path="/settings/verification" element={<ProtectedRoute><SettingsVerification /></ProtectedRoute>} />
        <Route path="/seller/verification" element={<ProtectedRoute><SellerVerification /></ProtectedRoute>} />
        <Route path="/seller/products" element={<ProtectedRoute><SellerProducts /></ProtectedRoute>} />
        <Route path="/seller/courses" element={<ProtectedRoute><SellerCourses /></ProtectedRoute>} />
        <Route path="/courses/:id/player" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
        <Route path="/settings/language" element={<ProtectedRoute><SettingsLanguage /></ProtectedRoute>} />
        <Route path="/settings/appearance" element={<ProtectedRoute><SettingsAppearance /></ProtectedRoute>} />
        <Route path="/settings/:key" element={<ProtectedRoute><SettingsPlaceholder /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
        <Route path="/barberos/cerca" element={<ProtectedRoute><BarbersNearby /></ProtectedRoute>} />
        <Route path="/profile/:type" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
        <Route path="/learn" element={<Navigate to="/shop?tab=courses" replace />} />
        <Route path="/tutorial/:tutorialId" element={<Navigate to="/shop?tab=courses" replace />} />
        <Route path="/shop" element={<ShopRedirect />} />
        <Route path="/membresia" element={<ProtectedRoute><MembershipSettings /></ProtectedRoute>} />
        <Route path="/conectar-claude" element={<ProtectedRoute><ConnectClaude /></ProtectedRoute>} />
        <Route path="/ajustes/precio-tarjeta" element={<ProtectedRoute><CardPricingSettings /></ProtectedRoute>} />
        <Route path="/ajustes/letrero" element={<ProtectedRoute><CardPricingSign /></ProtectedRoute>} />
        <Route path="/oauth/autorizar" element={<OAuthAuthorize />} />
        {/* M5 — carrito por usuario y checkout con candado legal */}
        <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        {/* Client-focused experience */}
        <Route path="/client" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
        <Route path="/liked" element={<ProtectedRoute><LikedHaircuts /></ProtectedRoute>} />
        <Route path="/regalos/historial" element={<ProtectedRoute><GiftHistory /></ProtectedRoute>} />
        <Route path="/tokens" element={<ProtectedRoute><TokenWallet /></ProtectedRoute>} />
        <Route path="/leagues" element={<ProtectedRoute><Leagues /></ProtectedRoute>} />
        <Route path="/leagues/campeones" element={<ProtectedRoute><ChampionsWall /></ProtectedRoute>} />
        <Route path="/leagues/:leagueId/escalera" element={<ProtectedRoute><LeagueLadder /></ProtectedRoute>} />
        <Route path="/credits" element={<ProtectedRoute><CreditsStore /></ProtectedRoute>} />
        <Route path="/reto/:id" element={<ProtectedRoute><ChallengeDetail /></ProtectedRoute>} />
        <Route path="/ranking/apoyo" element={<ProtectedRoute><SupportRanking /></ProtectedRoute>} />
        <Route path="/ranking/ganadores" element={<ProtectedRoute><TopEarners /></ProtectedRoute>} />
        <Route path="/apply-model" element={<ProtectedRoute><ModelApplication /></ProtectedRoute>} />
        <Route path="/applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
        <Route path="/judge-training" element={<ProtectedRoute><JudgeTraining /></ProtectedRoute>} />
        {/* Panel admin real por torneo: Finanzas / Check-in / Premios */}
        <Route path="/tournament/:tournamentId/admin" element={<ProtectedRoute><AdminTournamentControl /></ProtectedRoute>} />
        {/* Fase 2C-6 — Bracket Live PÚBLICO (visibilidad gestionada por backend, PD-42) */}
        <Route path="/tournament/:tournamentId/bracket-live" element={<BracketLive />} />
        <Route path="/tournament/:tournamentId/ops" element={<ProtectedRoute><TournamentOps /></ProtectedRoute>} />
        <Route path="/tournament/:tournamentId/ko-judge" element={<ProtectedRoute><KoJudgePanel /></ProtectedRoute>} />
        <Route path="/tournament/:tournamentId/group-judge" element={<ProtectedRoute><GroupJudgePanel /></ProtectedRoute>} />
        {/* Historial del juez (E2) — detalle SOLO LECTURA de torneo cerrado */}
        <Route path="/tournament/:tournamentId/judge-history" element={<ProtectedRoute><JudgeHistoryDetail /></ProtectedRoute>} />
        {/* ROL DE JUEZ Fase 1 — home del juez: SOLO torneos asignados */}
        <Route path="/judge" element={<ProtectedRoute><JudgeHome /></ProtectedRoute>} />
        <Route path="/judge/onboarding" element={<ProtectedRoute><JudgeOnboarding /></ProtectedRoute>} />
        <Route path="/judge/reputation" element={<ProtectedRoute><JudgeMyReputation /></ProtectedRoute>} />
        <Route path="/judges/reputation" element={<ProtectedRoute><JudgeReputation /></ProtectedRoute>} />
        {/* Evidencia competitiva — participante (móvil) y revisión staff */}
        <Route path="/tournament/:tournamentId/evidence" element={<ProtectedRoute><EvidenceUpload /></ProtectedRoute>} />
        <Route path="/tournament/:tournamentId/evidence-review" element={<ProtectedRoute><EvidenceReview /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <GlobalCreateButton />
      <AgendaFab />
      <FeedbackButton />
      <InstallBanner />
      <InstallGuideDialog />
      <DropLayer />
      <LegalAcceptanceGate />
      <PasswordRequiredDialog />
      <StepUpDialog />
      <AnalyticsConsentBanner />
      <ConsentPrompt />
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--foreground))',
            border: '1px solid hsl(var(--border))',
          },
        }}
      />
    </>
  );
}

function LanguageSyncBridge() {
  const { user } = useAuth();
  return <LanguageAccountSync user={user} />;
}

function App() {
  return (
    <AppErrorBoundary>
    <LanguageProvider>
      <AuthProvider>
        <ThemeProvider>
          <UploadCenterProvider>
            <CartProvider>
              <FeatureFlagsProvider>
                <PwaInstallProvider>
                  <LanguageSyncBridge />
                  <LanguageRemount>
                    <BrowserRouter>
                      <AppContent />
                    </BrowserRouter>
                  </LanguageRemount>
                </PwaInstallProvider>
              </FeatureFlagsProvider>
            </CartProvider>
          </UploadCenterProvider>
        </ThemeProvider>
      </AuthProvider>
    </LanguageProvider>
    </AppErrorBoundary>
  );
}

export default App;
